/**
 * Password validation tokens — rules, i18n keys, and strength hook.
 * Shared between RegisterPage and ResetPasswordForm.
 * @layer-1 — shared utilities.
 */
import { useMemo } from "react";

/** Five validation rules — must match both client (zod) and server. */
export const PASSWORD_RULES: readonly RegExp[] = [
  /.{8,}/,                           // min 8 chars
  /[0-9]/,                           // digit
  /[a-z]/,                           // lowercase
  /[A-Z]/,                           // uppercase
  /[@!#$%^&*()_+\-=[\]{}|;:'",.<>/?\\-]/, // special
];

export const PASSWORD_KEYS: readonly string[] = [
  "resetPassword.errors.passwordReqLength",
  "resetPassword.errors.passwordReqNumber",
  "resetPassword.errors.passwordReqLower",
  "resetPassword.errors.passwordReqUpper",
  "resetPassword.errors.passwordReqSpecial",
] as const;

/** i18n keys for RegisterPage password requirement checklist. */
export const REGISTER_PASSWORD_KEYS: readonly string[] = [
  "register.errors.passwordReqLength",
  "register.errors.passwordReqUpper",
  "register.errors.passwordReqLower",
  "register.errors.passwordReqNumber",
  "register.errors.passwordReqSpecial",
] as const;

export type StrengthScore = 0 | 1 | 2 | 3 | 4 | 5;

/** Calculates password strength score. */
export function usePasswordStrength(password: string): { score: StrengthScore; met: boolean[] } {
  return useMemo(() => {
    const met = PASSWORD_RULES.map((rule) => rule.test(password));
    return {
      score: met.filter(Boolean).length as StrengthScore,
      met,
    };
  }, [password]);
}